import React, { useEffect, useMemo, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import axios from 'axios';
import API_ROUTES from '../../../api';
import Icon from '../../../icon';

// 点球记录在单层底部面板中完成队伍、球员、结果与进球方式选择。
const PenaltyRecordPopup = ({
    visible,
    gameDetail,
    memberListInfo,
    getCurrentPlayingMemberIds,
    onClose,
    onUpdated,
}) => {
    const [team, setTeam] = useState(null);
    const [memberId, setMemberId] = useState(null);
    const [result, setResult] = useState(null);
    const [foot, setFoot] = useState(null);
    const [charts, setCharts] = useState([]);
    const [loadingCharts, setLoadingCharts] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setTeam(null);
        setMemberId(null);
        setResult(null);
        setFoot(null);
        setLoadingCharts(true);
        axios.get(API_ROUTES.game_charts_goal, {})
            .then(response => setCharts(response.data.data || []))
            .catch(() => Toast.show({ icon: 'fail', content: '获取本赛季进球数据失败！' }))
            .finally(() => setLoadingCharts(false));
    }, [visible]);

    const players = useMemo(() => {
        if (!team) return [];
        const playingIds = getCurrentPlayingMemberIds(team);
        const chartMap = new Map(charts.map(item => [String(item.id), {
            score: Number(item.score || 0),
            penaltyGoals: Number(item.penaltyGoals || 0),
        }]));

        return (memberListInfo || [])
            .filter(item => item.gameType === 0 && item.gameTeam === team && item.tempMember === null)
            .map(member => {
                const chart = chartMap.get(String(member.id));
                return {
                    ...member,
                    score: chart?.score ?? 0,
                    penaltyGoals: chart?.penaltyGoals ?? 0,
                    onField: playingIds.has(String(member.id)),
                    birthdayDate: new Date(member.birthday),
                };
            })
            .sort((a, b) => {
                if (a.onField !== b.onField) return a.onField ? -1 : 1;
                return a.score === b.score ? a.birthdayDate - b.birthdayDate : a.score - b.score;
            });
    }, [charts, getCurrentPlayingMemberIds, memberListInfo, team]);

    const chooseTeam = value => {
        setTeam(value);
        setMemberId(null);
        setResult(null);
        setFoot(null);
    };

    const submit = async () => {
        if (!team || !memberId || !result) {
            Toast.show({ icon: 'fail', content: '请选择队伍、球员和点球结果' });
            return;
        }
        if (result === 'goal' && !foot) {
            Toast.show({ icon: 'fail', content: '请选择左脚或右脚进球' });
            return;
        }

        const eventId = result === 'miss' ? 9 : foot;
        setSubmitting(true);
        try {
            const response = await axios.post(API_ROUTES.game_event, {
                gameId: gameDetail.id,
                memberId,
                eventId,
            });
            if (response.data.code === 200) {
                Toast.show({ icon: 'success', content: '点球记录成功！' });
                onClose();
                onUpdated();
            } else {
                Toast.show({ icon: 'fail', content: response.data.msg });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: '点球记录失败，请重试！' });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedMember = players.find(item => item.id === memberId);

    return (
        <Popup
            visible={visible}
            onMaskClick={onClose}
            bodyStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '90vh', overflow: 'hidden' }}
            style={{ '--z-index': 1400 }}
        >
            <div className="penalty-sheet">
                <div className="penalty-sheet-header">
                    <button type="button" onClick={onClose}>取消</button>
                    <div>
                        <div className="penalty-sheet-title">点球记录</div>
                        <div className="penalty-sheet-subtitle">选择执行者并确认点球结果</div>
                    </div>
                    <button type="button" className="primary" disabled={submitting} onClick={submit}>确认</button>
                </div>

                <div className="penalty-sheet-content">
                    <div className="penalty-section-title">1. 选择队伍</div>
                    <div className="penalty-team-grid">
                        {[
                            { value: 1, label: gameDetail?.greenName || '绿队', color: gameDetail?.greenColor || '#7c8cff' },
                            { value: 2, label: gameDetail?.redName || '红队', color: gameDetail?.redColor || '#ff5f68' },
                        ].map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={team === item.value ? 'active' : ''}
                                style={{ '--penalty-team-color': item.color }}
                                onClick={() => chooseTeam(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="penalty-section-title">2. 选择执行球员</div>
                    {loadingCharts ? (
                        <div className="penalty-loading">正在读取本赛季进球数据…</div>
                    ) : team ? (
                        <div className="penalty-player-list">
                            {players.map(member => (
                                <button
                                    key={member.id}
                                    type="button"
                                    className={memberId === member.id ? 'active' : ''}
                                    onClick={() => {
                                        setMemberId(member.id);
                                        setResult(null);
                                        setFoot(null);
                                    }}
                                >
                                    <span className="penalty-player-main">
                                        <strong>{member.name}</strong>
                                        {member.onField && <em>在场</em>}
                                    </span>
                                    <span className="penalty-player-stats">赛季进球 {member.score} · 点球 {member.penaltyGoals}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="penalty-empty">请先选择队伍</div>
                    )}

                    <div className="penalty-section-title">3. 确认点球结果</div>
                    <div className="penalty-result-grid">
                        <button type="button" className={result === 'goal' ? 'goal active' : 'goal'} disabled={!memberId} onClick={() => setResult('goal')}>
                            <img src={Icon.PENALTY_KICK} alt="" />点球进球
                        </button>
                        <button type="button" className={result === 'miss' ? 'miss active' : 'miss'} disabled={!memberId} onClick={() => { setResult('miss'); setFoot(null); }}>
                            <img src={Icon.PENALTY_NOT_SCORED} alt="" />点球未进
                        </button>
                    </div>

                    {result === 'goal' && (
                        <div className="penalty-foot-grid">
                            <button type="button" className={foot === 21 ? 'active' : ''} onClick={() => setFoot(21)}>左脚球</button>
                            <button type="button" className={foot === 12 ? 'active' : ''} onClick={() => setFoot(12)}>右脚球</button>
                        </div>
                    )}

                    {selectedMember && (
                        <div className="penalty-summary">
                            当前选择：{selectedMember.name} · {result === 'goal' ? '点球进球' : result === 'miss' ? '点球未进' : '待选择结果'}
                        </div>
                    )}
                </div>
            </div>
        </Popup>
    );
};

export default PenaltyRecordPopup;
